import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import {
    appendFieldError,
    createDropdown,
    createField,
    createInputField,
    disableFormControls
} from '../forms/formFields';
import {createRemoveIcon} from '../forms/repeatableFields';
import {cloneValue} from '../forms/formState';

const defaultMCPServer = {
    credentials: '',
    transportType: 'StreamableHTTP',
    serverUrl: '',
    requestTimeoutSeconds: null,
    toolFilter: []
};

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';
const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const normalizeString = value => typeof value === 'undefined' || value === null ? '' : String(value);

const normalizeInteger = value => {
    if (typeof value === 'undefined' || value === null || value === '') {
        return null;
    }

    const numberValue = Number.parseInt(value, 10);
    return Number.isNaN(numberValue) ? value : numberValue;
};

const normalizeStringList = value => Array.isArray(value)
    ? value.map(item => typeof item === 'undefined' || item === null ? '' : String(item)).filter(item => item.length > 0)
    : value;

const normalizeDiscoveredTools = tools => {
    if (!Array.isArray(tools)) {
        return [];
    }

    const normalizedTools = new Map();
    tools.forEach(tool => {
        const name = normalizeString(tool?.name).trim();
        if (name.length === 0 || normalizedTools.has(name)) {
            return;
        }

        normalizedTools.set(name, {
            name,
            description: normalizeString(tool?.description).trim()
        });
    });
    return [...normalizedTools.values()];
};

const normalizeMCPServer = server => {
    if (!isPlainObject(server)) {
        return {};
    }

    const normalizedServer = cloneValue(server);
    ['credentials', 'transportType', 'serverUrl'].forEach(fieldName => {
        if (hasOwnProperty(server, fieldName)) {
            normalizedServer[fieldName] = normalizeString(server[fieldName]);
        }
    });

    if (hasOwnProperty(server, 'requestTimeoutSeconds')) {
        normalizedServer.requestTimeoutSeconds = normalizeInteger(server.requestTimeoutSeconds);
    }

    if (hasOwnProperty(server, 'toolFilter')) {
        normalizedServer.toolFilter = normalizeStringList(server.toolFilter);
    }

    return normalizedServer;
};

const normalizeMCPServers = servers => Array.isArray(servers) ? servers.map(normalizeMCPServer) : servers;

const normalizeMCPServerPatch = (server, patch) => {
    const normalizedPatch = cloneValue(patch || {});

    Object.keys(normalizedPatch).forEach(fieldName => {
        const value = normalizedPatch[fieldName];
        const emptyArray = Array.isArray(value) && value.length === 0;
        if (value !== null && value !== '' && !emptyArray) {
            return;
        }

        if (!hasOwnProperty(server, fieldName)) {
            delete normalizedPatch[fieldName];
        }
    });

    return normalizedPatch;
};

const getResource = (mxResources, key) => mxResources.get(key);

const fieldValue = (server, fieldName) => normalizeString(server[fieldName]);

const numberFieldValue = (server, fieldName) => {
    const value = server[fieldName];
    return typeof value === 'undefined' || value === null ? '' : String(value);
};

const listFieldValue = (server, fieldName) => Array.isArray(server[fieldName]) ? server[fieldName] : [];

const serializeDropdownValues = values => normalizeStringList(values).join(',');

const deserializeDropdownValues = value => normalizeStringList(String(value || '').split(','));

const arrayError = error => Array.isArray(error) ? error.filter(Boolean) : error;

const toolFilterOptions = (selectedValues, discoveredTools) => {
    const options = new Map();
    selectedValues.forEach(value => {
        options.set(value, {name: value, description: ''});
    });
    discoveredTools.forEach(tool => {
        options.set(tool.name, tool);
    });
    return [...options.values()];
};

const createToolFilterItem = tool => {
    const item = document.createElement('div');
    item.className = tool.description ? 'vertical item' : 'item';
    item.dataset.value = tool.name;
    if (tool.description) {
        const description = document.createElement('div');
        description.className = 'description';
        description.textContent = tool.description;
        item.appendChild(description);
    }
    const text = document.createElement('span');
    text.className = 'text';
    text.textContent = tool.name;
    item.appendChild(text);
    return item;
};

const discoveredToolsStatusText = (mxResources, count) => {
    if (count === 0) {
        return getResource(mxResources, 'mcpServerToolsEmpty');
    }

    const resourceKey = count === 1 ? 'mcpServerToolDiscovered' : 'mcpServerToolsDiscovered';
    return `${count} ${getResource(mxResources, resourceKey)}`;
};

const discoverySourceFields = new Set([
    'credentials',
    'transportType',
    'serverUrl',
    'requestTimeoutSeconds'
]);

const normalizeDiscoveredToolsCacheMap = entries => new Map(Array.isArray(entries)
    ? entries.map(([index, value]) => [index, normalizeDiscoveredTools(value)])
    : []);

const normalizeDiscoveryErrorCacheMap = entries => new Map(Array.isArray(entries)
    ? entries.map(([index, value]) => [index, normalizeString(value)])
    : []);

const createCredentialOptions = (mxResources, value) => {
    const options = [{
        value: '',
        name: getResource(mxResources, 'credentialsSelect')
    }];

    if (!isEmpty(value)) {
        options.push({
            value,
            name: value
        });
    }

    return options;
};

const createButton = (label, className = 'ui mini compact button') => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    return button;
};

const hasDiscoverySourceChange = patch => Object.keys(patch || {})
    .some(fieldName => discoverySourceFields.has(fieldName));

const createMCPServersForm = ({
                                  mcpServers,
                                  mcpServerErrors = [],
                                  editable = true,
                                  mxResources,
                                  mountElement = null,
                                  createActionField = null,
                                  toolDiscoveryCache = {},
                                  onRender = () => {},
                                  onChange = () => {}
                              } = {}) => {
    let currentMCPServers = normalizeMCPServers(mcpServers);
    let discoveredToolsByIndex = normalizeDiscoveredToolsCacheMap(toolDiscoveryCache.toolsByIndex);
    let discoveryErrorsByIndex = normalizeDiscoveryErrorCacheMap(toolDiscoveryCache.errorsByIndex);

    const container = mountElement || document.createElement('div');
    container.innerHTML = '';
    if (!mountElement) {
        container.className = 'ui form mcp-servers-form';
    }

    const addButton = createButton(getResource(mxResources, 'aiChatAddMCPServer'));
    addButton.addEventListener('click', () => {
        const servers = Array.isArray(currentMCPServers) ? currentMCPServers : [];
        currentMCPServers = [...servers, cloneValue(defaultMCPServer)];
        emitMCPServers();
        render();
    });

    const updateServer = (index, patch) => {
        const servers = Array.isArray(currentMCPServers) ? currentMCPServers : [];
        const currentServer = servers[index] || {};
        const normalizedPatch = normalizeMCPServerPatch(currentServer, patch);
        const nextMCPServers = servers.map((server, serverIndex) => serverIndex === index
            ? normalizeMCPServer({...server, ...normalizedPatch})
            : server);
        if (isEqual(currentMCPServers, nextMCPServers)) {
            return;
        }

        currentMCPServers = nextMCPServers;
        const discoverySourceChanged = hasDiscoverySourceChange(normalizedPatch);
        if (discoverySourceChanged) {
            discoveredToolsByIndex.delete(index);
            discoveryErrorsByIndex.delete(index);
        }
        emitMCPServers();
        if (discoverySourceChanged) {
            render();
        }
    };

    const removeServer = index => {
        sync();
        const servers = Array.isArray(currentMCPServers) ? currentMCPServers : [];
        currentMCPServers = servers.filter((server, serverIndex) => serverIndex !== index);
        discoveredToolsByIndex = new Map([...discoveredToolsByIndex.entries()]
            .filter(([serverIndex]) => serverIndex !== index)
            .map(([serverIndex, tools]) => [serverIndex > index ? serverIndex - 1 : serverIndex, tools]));
        discoveryErrorsByIndex = new Map([...discoveryErrorsByIndex.entries()]
            .filter(([serverIndex]) => serverIndex !== index)
            .map(([serverIndex, error]) => [serverIndex > index ? serverIndex - 1 : serverIndex, error]));
        render();
        emitMCPServers();
    };

    const createServerFields = (server, index) => {
        const errors = Array.isArray(mcpServerErrors) ? mcpServerErrors[index] : null;
        const segment = document.createElement('div');
        segment.className = 'ui segment mcp-server';

        const header = document.createElement('div');
        header.className = 'ui small clearing header removable-section-header';

        if (editable) {
            const removeControl = createRemoveIcon(getResource(mxResources, 'aiChatRemoveMCPServer'));
            const removeCurrentServer = () => removeServer(index);
            removeControl.addEventListener('mousedown', event => event.preventDefault?.());
            removeControl.addEventListener('click', removeCurrentServer);
            removeControl.addEventListener('keydown', event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault?.();
                    removeCurrentServer();
                }
            });
            header.appendChild(removeControl);
        }
        header.appendChild(document.createTextNode(getResource(mxResources, 'mcpServer')));
        segment.appendChild(header);

        if (typeof errors === 'string') {
            const serverError = document.createElement('div');
            serverError.className = 'ui basic error large pointing prompt label mcp-server-error';
            serverError.textContent = errors;
            segment.appendChild(serverError);
        }

        const credentialsField = createDropdown({
            label: getResource(mxResources, 'credentials'),
            id: `mcpServerCredentials${index}`,
            name: 'mcpServers.credentials',
            value: fieldValue(server, 'credentials'),
            clearable: true,
            searchable: true,
            error: errors?.credentials,
            options: createCredentialOptions(mxResources, fieldValue(server, 'credentials'))
        });
        const credentialsSelect = credentialsField.querySelector('select');
        credentialsSelect.className += ' credentials';
        credentialsSelect.value = fieldValue(server, 'credentials');
        credentialsSelect.addEventListener('change', event => updateServer(index, {credentials: event.target.value}));
        segment.appendChild(credentialsField);

        if (fieldValue(server, 'transportType') === 'SSE') {
            const warning = document.createElement('div');
            warning.className = 'ui warning message mcp-sse-warning';
            warning.textContent = getResource(mxResources, 'mcpServerSSEUnsupported');
            if (editable) {
                const switchButton = createButton(getResource(mxResources, 'mcpServerSwitchToStreamableHTTP'));
                switchButton.addEventListener('click', () => {
                    updateServer(index, {
                        transportType: 'StreamableHTTP'
                    });
                });
                warning.appendChild(switchButton);
            }
            segment.appendChild(warning);
        }
        if (errors?.transportType) {
            appendFieldError(segment, errors.transportType, {className: 'mcp-transport-error'});
        }

        const serverUrlField = createInputField({
            label: getResource(mxResources, 'mcpServerUrl'),
            id: `mcpServerUrl${index}`,
            name: 'mcpServers.serverUrl',
            type: 'text',
            className: 'required',
            value: fieldValue(server, 'serverUrl'),
            error: errors?.serverUrl
        });
        serverUrlField.querySelector('input').addEventListener('change', event => updateServer(index, {serverUrl: event.target.value}));
        segment.appendChild(serverUrlField);

        const requestTimeoutSecondsField = createInputField({
            label: getResource(mxResources, 'mcpServerRequestTimeoutSeconds'),
            id: `mcpServerRequestTimeoutSeconds${index}`,
            name: 'mcpServers.requestTimeoutSeconds',
            type: 'number',
            min: 1,
            value: numberFieldValue(server, 'requestTimeoutSeconds'),
            error: errors?.requestTimeoutSeconds
        });
        requestTimeoutSecondsField.querySelector('input').addEventListener('change', event => updateServer(index, {requestTimeoutSeconds: normalizeInteger(event.target.value)}));
        segment.appendChild(requestTimeoutSecondsField);

        const selectedToolFilterValues = listFieldValue(server, 'toolFilter');
        const discoveredToolsAvailable = discoveredToolsByIndex.has(index);
        const discoveredTools = discoveredToolsByIndex.get(index) || [];
        const toolFilterDropdown = document.createElement('div');
        toolFilterDropdown.id = `mcpServerToolFilter${index}`;
        toolFilterDropdown.className = `ui fluid search selection multiple dropdown addition mcp-tool-filter-dropdown${editable ? '' : ' disabled'}`;
        toolFilterDropdown.dataset.allowAdditions = 'true';
        toolFilterDropdown.dataset.mcpServerIndex = String(index);
        toolFilterDropdown.dataset.mcpServerCredentials = fieldValue(server, 'credentials');
        toolFilterDropdown.dataset.mcpServerTransportType = fieldValue(server, 'transportType') === 'SSE'
            ? ''
            : fieldValue(server, 'transportType');
        toolFilterDropdown.dataset.mcpServerUrl = fieldValue(server, 'serverUrl');
        toolFilterDropdown.dataset.mcpServerRequestTimeoutSeconds = numberFieldValue(server, 'requestTimeoutSeconds');
        if (discoveredToolsAvailable) {
            toolFilterDropdown.dataset.mcpToolsDiscovered = 'true';
        }
        toolFilterDropdown.addEventListener('mcp-tools-discovered', event => {
            discoveredToolsByIndex.set(index, normalizeDiscoveredTools(event.detail?.tools));
            discoveryErrorsByIndex.delete(index);
            if (event.detail?.render !== false) {
                render();
            }
        });
        toolFilterDropdown.addEventListener('mcp-tools-discovery-error', event => {
            const message = normalizeString(event.detail?.message);
            if (message) {
                discoveryErrorsByIndex.set(index, message);
            } else {
                discoveryErrorsByIndex.delete(index);
            }
            render();
        });
        const toolFilterInput = document.createElement('input');
        toolFilterInput.type = 'hidden';
        toolFilterInput.name = 'mcpServers.toolFilter';
        toolFilterInput.value = serializeDropdownValues(selectedToolFilterValues);
        toolFilterInput.addEventListener('change', event => {
            const target = event.target;
            if (target.disabled || target.readOnly) {
                return;
            }

            const value = target.value;
            setTimeout(() => {
                if (target.isConnected === false) {
                    return;
                }
                updateServer(index, {toolFilter: deserializeDropdownValues(value)});
            });
        });
        toolFilterDropdown.appendChild(toolFilterInput);
        const toolFilterIcon = document.createElement('i');
        toolFilterIcon.className = 'dropdown icon';
        toolFilterDropdown.appendChild(toolFilterIcon);
        const toolFilterDefaultText = document.createElement('div');
        toolFilterDefaultText.className = 'default text';
        toolFilterDefaultText.textContent = getResource(mxResources, 'mcpServerToolFilterPlaceholder');
        toolFilterDropdown.appendChild(toolFilterDefaultText);
        const toolFilterMenu = document.createElement('div');
        toolFilterMenu.className = 'menu';
        toolFilterOptions(selectedToolFilterValues, discoveredTools)
            .forEach(tool => toolFilterMenu.appendChild(createToolFilterItem(tool)));
        toolFilterDropdown.appendChild(toolFilterMenu);
        const toolFilterField = createField({
            label: getResource(mxResources, 'mcpServerToolFilter'),
            error: arrayError(errors?.toolFilter),
            fieldElement: toolFilterDropdown
        });
        segment.appendChild(toolFilterField);

        if (discoveredToolsAvailable) {
            const toolsStatus = document.createElement('div');
            toolsStatus.className = 'ui top pointing basic large label mcp-tool-discovery-status';
            toolsStatus.textContent = discoveredToolsStatusText(mxResources, discoveredTools.length);
            toolFilterField.appendChild(toolsStatus);
        }

        if (discoveryErrorsByIndex.has(index)) {
            appendFieldError(toolFilterField, discoveryErrorsByIndex.get(index), {
                className: 'mcp-tool-discovery-error'
            });
        }

        return segment;
    };

    function render() {
        container.innerHTML = '';
        if (typeof mcpServerErrors === 'string') {
            const mcpServersError = document.createElement('div');
            mcpServersError.className = 'ui basic error large pointing prompt label mcp-servers-error';
            mcpServersError.textContent = mcpServerErrors;
            container.appendChild(mcpServersError);
        }
        if (Array.isArray(currentMCPServers)) {
            currentMCPServers.forEach((server, index) => container.appendChild(createServerFields(server, index)));
        }
        if (editable) {
            const actions = document.createElement('div');
            actions.className = 'mcp-servers-actions';
            actions.appendChild(addButton);
            const actionField = createActionField?.();
            if (actionField) {
                actions.appendChild(actionField);
            }
            container.appendChild(actions);
        } else {
            disableFormControls(container);
        }
        onRender(container);
    }

    function emitMCPServers() {
        onChange(cloneValue(currentMCPServers));
    }

    const sync = () => {
        if (!editable || !Array.isArray(currentMCPServers)) {
            return;
        }
        const inputs = container.querySelectorAll('input[name="mcpServers.serverUrl"]');
        const nextServers = currentMCPServers.map((server, index) => {
            const value = inputs[index]?.value;
            if (typeof value === 'undefined' || value === fieldValue(server, 'serverUrl')) {
                return server;
            }
            discoveredToolsByIndex.delete(index);
            discoveryErrorsByIndex.delete(index);
            return {...server, serverUrl: value};
        });
        if (!isEqual(currentMCPServers, nextServers)) {
            currentMCPServers = nextServers;
            emitMCPServers();
        }
    };

    render();

    return {
        element: container,
        sync,
        getMCPServers: () => {
            sync();
            return cloneValue(currentMCPServers);
        },
        getToolDiscoveryCache: () => ({
            toolsByIndex: [...discoveredToolsByIndex.entries()].map(([index, tools]) => [index, cloneValue(tools)]),
            errorsByIndex: [...discoveryErrorsByIndex.entries()]
        }),
        setMCPServers: value => {
            currentMCPServers = normalizeMCPServers(value);
            discoveredToolsByIndex = new Map();
            discoveryErrorsByIndex = new Map();
            render();
        }
    };
};

export {
    createMCPServersForm,
    defaultMCPServer,
    normalizeDiscoveredTools,
    normalizeMCPServer,
    normalizeMCPServerPatch,
    normalizeMCPServers
};
